import * as React from "react";
import { useState, useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { KanbanColumn } from "./KanbanColumn";
import { FeatureCard, FeatureCardOverlay } from "./FeatureCard";
import { trpc } from "@/lib/trpc";

type FeatureStatus = "planning" | "in_progress" | "completed" | "on_hold";

interface Feature {
  id: string;
  name: string;
  description: string | null;
  status: FeatureStatus;
  priority: "low" | "medium" | "high" | "urgent";
  order: number | null;
  taskCount: number;
}

interface KanbanBoardProps {
  features: Feature[];
  searchQuery: string;
  onFeatureClick: (id: string) => void;
}

const columns: { id: FeatureStatus; title: string }[] = [
  { id: "planning", title: "Planning" },
  { id: "in_progress", title: "In Progress" },
  { id: "completed", title: "Completed" },
  { id: "on_hold", title: "On Hold" },
];

export function KanbanBoard({ features, searchQuery, onFeatureClick }: KanbanBoardProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [localFeatures, setLocalFeatures] = useState<Feature[]>(features);
  const utils = trpc.useUtils();

  // Keep local state in sync with props
  React.useEffect(() => {
    setLocalFeatures(features);
  }, [features]);

  const reorderMutation = trpc.feature.reorder.useMutation({
    onError: () => {
      // Rollback on error
      setLocalFeatures(features);
      utils.feature.list.invalidate();
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const filtered = useMemo(() => {
    if (!searchQuery) return localFeatures;
    const q = searchQuery.toLowerCase();
    return localFeatures.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        (f.description && f.description.toLowerCase().includes(q)),
    );
  }, [localFeatures, searchQuery]);

  const columnData = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      items: filtered
        .filter((f) => f.status === col.id)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }));
  }, [filtered]);

  const activeFeature = activeId ? localFeatures.find((f) => f.id === activeId) : null;

  function findColumn(featureId: string): FeatureStatus | null {
    const feature = localFeatures.find((f) => f.id === featureId);
    return feature?.status ?? null;
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) return;

    const activeFeatureId = active.id as string;
    const overId = over.id as string;

    // Determine target column
    let targetColumn: FeatureStatus | null = null;

    // Check if dropping over a column
    if (columns.some((c) => c.id === overId)) {
      targetColumn = overId as FeatureStatus;
    } else {
      // Dropping over another feature card
      targetColumn = findColumn(overId);
    }

    if (!targetColumn) return;

    const currentColumn = findColumn(activeFeatureId);
    if (currentColumn === targetColumn) return;

    // Optimistically move feature to new column
    setLocalFeatures((prev) =>
      prev.map((f) => (f.id === activeFeatureId ? { ...f, status: targetColumn! } : f)),
    );
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeFeatureId = active.id as string;
    const overId = over.id as string;

    // Determine target column and position
    let targetColumn: FeatureStatus | null = null;

    if (columns.some((c) => c.id === overId)) {
      targetColumn = overId as FeatureStatus;
    } else {
      targetColumn = findColumn(overId);
    }

    if (!targetColumn) return;

    // Get all features in the target column
    const columnFeatures = localFeatures
      .filter((f) => f.status === targetColumn && f.id !== activeFeatureId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Find insertion index
    let insertIndex = columnFeatures.length;
    if (overId !== targetColumn) {
      const overIndex = columnFeatures.findIndex((f) => f.id === overId);
      if (overIndex >= 0) insertIndex = overIndex;
    }

    // Insert active feature at the right position
    columnFeatures.splice(insertIndex, 0, {
      ...localFeatures.find((f) => f.id === activeFeatureId)!,
      status: targetColumn,
    });

    // Create reorder items with new order values
    const reorderItems = columnFeatures.map((f, i) => ({
      id: f.id,
      order: i,
      ...(f.id === activeFeatureId ? { status: targetColumn! } : {}),
    }));

    // Update local state
    setLocalFeatures((prev) =>
      prev.map((f) => {
        const reordered = reorderItems.find((r) => r.id === f.id);
        if (reordered) {
          return {
            ...f,
            order: reordered.order,
            ...(reordered.status ? { status: reordered.status } : {}),
          };
        }
        return f;
      }),
    );

    // Persist to server
    reorderMutation.mutate({ items: reorderItems });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 lg:grid lg:grid-cols-4 lg:overflow-visible">
        {columnData.map((col) => (
          <KanbanColumn
            key={col.id}
            id={col.id}
            title={col.title}
            count={col.items.length}
            itemIds={col.items.map((f) => f.id)}
          >
            {col.items.map((feature) => (
              <FeatureCard
                key={feature.id}
                id={feature.id}
                name={feature.name}
                description={feature.description}
                taskCount={feature.taskCount}
                priority={feature.priority}
                onClick={() => onFeatureClick(feature.id)}
              />
            ))}
          </KanbanColumn>
        ))}
      </div>

      <DragOverlay>
        {activeFeature ? (
          <FeatureCardOverlay
            name={activeFeature.name}
            description={activeFeature.description}
            taskCount={activeFeature.taskCount}
            priority={activeFeature.priority}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
